/**
 * The approved WhatsApp template an ad-hoc message to a passenger goes out
 * in — `we_have_a_message_for_you`, three blocks:
 *
 *     Hi {{1}},
 *
 *     We have a new message for you:
 *
 *     {{2}}
 *
 * `{{1}}` is the passenger's first name and `{{2}}` is the body. Only the
 * body is ours to write: BOTH the recipient's number and the greeting name
 * are derived from the booking by the server, and a client cannot set them.
 *
 * WHY THIS IS A MODULE AND NOT TWO BITS OF JSX. Two screens compose these
 * messages — the portal's dispatcher trip sheet and the dashboard's order
 * details — in two different style systems, so each renders the blocks with
 * its own classes. What must NOT differ between them is the words, their
 * order, and which of them the sender may edit: a sender who cannot see the
 * baked-in greeting types "Hi Anna" into the body and the passenger is
 * greeted twice. So the blocks are DATA here and markup there.
 *
 * `first` is passed in already reduced to a first name rather than derived
 * here, so this module stays free of any dependency on a screen's own
 * name-formatting (the portal's `firstName`, which both callers use).
 */

/**
 * The longest body the template will carry, and the API's own rule.
 *
 * Mirrors `PORTAL_MESSAGE_MAX_LENGTH` in the API's `Portal.schema.ts`:
 * `POST /portal/message` refuses a longer `text` with a 400 on that field.
 * It is not an arbitrary round number — Meta caps a template VARIABLE at
 * about 1024 characters and rejects an over-long send at its own edge, by
 * which point the passenger receives NOTHING. Refusing early instead lets
 * the office shorten the message while it still has it.
 *
 * So the cap belongs to the template rather than to either screen, and both
 * composers count against this one number. A composer should show the count
 * and stop the send, NOT truncate: silently dropping the end of somebody's
 * message loses the half that mattered.
 */
export const TEMPLATE_VARIABLE_MAX = 1000;

/** The template's own fixed second block. */
export const TEMPLATE_LEAD = 'We have a new message for you:';

/** The template's own first block, for a first name the server will also derive. */
export const templateGreeting = (first) => `Hi ${first},`;

/**
 * The three blocks as the passenger will read them, in order.
 *
 * `fixed` marks the two the template bakes in — a renderer shows them as
 * part of the message but not as something to type over. The body block
 * carries `empty` while nothing has been written, so a preview can stand in
 * a placeholder without pretending it is the text that will be sent.
 */
/**
 * Does this body still look like it has a VARIABLE in it?
 *
 * Saved messages may carry variables — `{pickup}`, `{airport_minutes}` — and
 * the server fills them in before a body ever reaches a composer. It also
 * withholds a saved message whose variables a booking cannot fill, and
 * refuses a send that still carries one, because a customer reading a
 * literal "{pickup}" is the whole thing this machinery exists to prevent.
 *
 * What is left is somebody typing one by hand, or an AI draft copying a
 * token off the management screen. Catching THAT here turns a 400 at the
 * worst possible moment into a line of advice while the message is still
 * being written.
 *
 * DELIBERATELY SHAPE-BASED AND NON-BLOCKING. The vocabulary belongs to the
 * server — the management screen is handed the list rather than keeping one
 * — so this matches the SHAPE `{lower_case_word}` instead of a copy of the
 * names that would go stale. A shape match is not proof: the send is still
 * armed and the server is still the authority. It only warns.
 *
 * @returns the first `{token}` found, or '' when there is none.
 */
export const looksUnresolved = (body) => (
  String(body ?? '').match(/\{[a-z][a-z0-9_]*\}/i)?.[0] || ''
);

export const templateBlocks = ({ first = '', body = '', placeholder = '' } = {}) => {
  const written = String(body ?? '').trim();

  return [
    {
      id: 'greeting',
      text: templateGreeting(first),
      fixed: true,
      empty: false,
    },
    {
      id: 'lead',
      text: TEMPLATE_LEAD,
      fixed: true,
      empty: false,
    },
    {
      id: 'body',
      text: written || placeholder,
      fixed: false,
      empty: !written,
    },
  ];
};

export default templateBlocks;
